import { cn } from "@/lib/utils";
import { useQuoteSettingsStore } from "@/store/quoteSettingsStore";
import { GHOST_FIELD_CLASSES } from "./GhostInput";

interface QuantityCellProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  className?: string;
}

/** Matches the quantity input, whose edits autosave defers until it blurs. */
export const QUANTITY_INPUT_SELECTOR = "[data-quantity-input]";

const WHOLE_QUANTITY_PATTERN = /^\d+$/;
const DECIMAL_QUANTITY_PATTERN = /^\d+(\.\d*)?$/;

/**
 * Quantity input that accepts decimals only when the org allows fractional quantities.
 * @param props - The quantity value, change handler, and display options
 * @returns The quantity input
 */
export function QuantityCell({
  value,
  onChange,
  disabled = false,
  className,
}: QuantityCellProps) {
  const allowFractionalQuantities = useQuoteSettingsStore(
    (state) => state.settings?.allowFractionalQuantities ?? true,
  );
  const quantityPattern = allowFractionalQuantities
    ? DECIMAL_QUANTITY_PATTERN
    : WHOLE_QUANTITY_PATTERN;
  return (
    <input
      type="text"
      inputMode={allowFractionalQuantities ? "decimal" : "numeric"}
      data-quantity-input
      value={value}
      onChange={(e) => {
        const raw = e.target.value;
        if (raw === "" || quantityPattern.test(raw)) {
          onChange(raw);
        }
      }}
      disabled={disabled}
      title={value}
      className={cn(
        "flex h-6 sm:h-8 w-full rounded-md px-1 sm:px-2 py-0.5 sm:py-1 text-xs sm:text-sm text-right tabular-nums transition-colors",
        "overflow-hidden text-ellipsis",
        GHOST_FIELD_CLASSES,
        "placeholder:text-muted-foreground",
        className,
      )}
    />
  );
}
